import React from 'react';
import rocketsvg from 'assets/img/rocket.svg';
import trophysvg from 'assets/img/trophy.svg';
import relaxedsvg from 'assets/img/relaxed.svg';
import questionsvg from 'assets/img/question.svg';

import i18n from 'i18n';
import Button from 'react-bootstrap/Button';
import { Col, Row } from 'react-bootstrap';
import SimpleLoader from 'components/loaders/SimpleLoader';
import CopyToClipboardButton from 'components/buttons/CopyToClipboardButton';
import { getBlazemeterBillingPageUrl, isUserAccountOwner } from 'utils/bzmUserConfigUtil';
import { Burble } from '../../ui/Burble/Burble';
import { WzState } from 'components/modal/wizard/WizardTransactionTypes';
import clsx from 'clsx';
import styles from '../WizardModal.module.css';
import BzmIcon from '../../icons/BzmIcon';

interface RunProps {
  status: WzState;
  exampleUrl: string;
  endpoint: string;
  mockService: string;
  service: string;
  location: string;
  transactions: number;
  runMockService: () => void;
  creationInProgress: boolean;
  deployInProgress: boolean;
  onClose: () => void;
  deployError: string;
  restart: () => void;
  doneButton?: any;
}

const Run = (props: RunProps) => {
  const getDoneButton = () => {
    if (props.doneButton) {
      return props.doneButton;
    } else {
      return (
        <Button onClick={props.onClose}>
          <span> {i18n.t('wizard:details.congrats.done')} </span>
        </Button>
      );
    }
  };

  const ready = () => {
    return (
      <div>
        <div style={{ height: '80px' }} />
        <div className="text-center">
          <h1>{i18n.t('wizard:details.run.title')}</h1>
          <div>
            <span> {i18n.t('wizard:details.run.message')} </span>
          </div>
        </div>

        {props.creationInProgress ? (
          <div className="mt-5 mb-5">
            <div className={clsx(styles.inProgress)}>
              <Row>
                <Col md={1}>
                  <i className="icon icon-information icon-sm icon-theme-gray-80"></i>
                </Col>
                <Col>
                  <span dangerouslySetInnerHTML={{ __html: i18n.t('wizard:details.run.pleaseWait') }} />
                </Col>
              </Row>
            </div>
          </div>
        ) : (
          <div style={{ height: '60px' }} />
        )}

        <div className="text-center">
          <Row>
            <Col className={'text-right'}>
              <img src={rocketsvg} alt={'rocket'} />
            </Col>
            <Col className={'text-left'}>
              <div>
                <b>{i18n.t('wizard:details.run.mockService')}</b>
                <span>{props.mockService}</span>
              </div>
              <br />
              <div>
                <b>{i18n.t('wizard:details.run.service')}</b>
                <span>{props.service}</span>
              </div>
              <br />
              <div>
                <b>{i18n.t('wizard:details.run.location')}</b>
                <span>{props.location}</span>
              </div>
              <br />
              <div>
                <b>{i18n.t('wizard:details.run.transactions')}</b>
                <span>{props.transactions}</span>
              </div>

              <br />
              <Button
                disabled={props.creationInProgress}
                className="btn btn-success"
                onClick={() => {
                  props.runMockService();
                }}
              >
                <BzmIcon variant={'icon-bzm-play-run'} className={'icon-xs inline-span-style'}></BzmIcon>
                {props.creationInProgress ? (
                  <>
                    <SimpleLoader className={'inline-span-style'} loadingText={''} />
                    <span className={'ml-2 inline-span-style'}>{i18n.t('wizard:details.run.startingMockService')}</span>
                  </>
                ) : (
                  <span className={'ml-2 inline-span-style'}>{i18n.t('wizard:details.run.runMockService')}</span>
                )}
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  const running = () => {
    const paragraph =
      props.exampleUrl !== ''
        ? i18n.t('wizard:details.congrats.paragraphLong')
        : i18n.t('wizard:details.congrats.paragraphShort');

    const copyButton = (endpoint: string, successMsg: string) => {
      return (
        <div>
          <CopyToClipboardButton
            id={'copy-widget-content'}
            textToCopy={endpoint}
            title={'Copy Endpoint'}
            customToastMsg={successMsg}
            paddingSize={1}
          >
            <div className={'hover-icon-link'}>
              {endpoint}
              <BzmIcon
                variant={'icon-bzm-clipboard'}
                className={'icon-xs text-blue'}
                style={{ position: 'absolute', marginLeft: '3px' }}
              />
            </div>
          </CopyToClipboardButton>
        </div>
      );
    };

    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <div className="text-center">
          <div className="text-center mb-5">
            <h1>{i18n.t('wizard:details.congrats.title')}</h1>
            <div>
              <span
                dangerouslySetInnerHTML={{
                  __html: i18n.t('wizard:details.congrats.message', {
                    name: props.mockService,
                  }),
                }}
              />
            </div>
          </div>

          <div className="text-center mb-5">
            <img src={trophysvg} alt={'trophy'} />
          </div>

          <div className="text-center">
            <p className={'mb-1'} dangerouslySetInnerHTML={{ __html: paragraph }}></p>
          </div>

          <div style={{ marginTop: 40 }} className="text-center">
            <Row>
              <Col md={1}></Col>
              {props.exampleUrl !== '' && (
                <Col md={5}>
                  <div style={{ overflowWrap: 'anywhere' }}>
                    {' '}
                    <b>{i18n.t('wizard:details.congrats.exampleRequest')}</b>{' '}
                    {copyButton(props.exampleUrl, i18n.t('wizard:details.congrats.copyExampleSuccessMsg'))}
                  </div>
                </Col>
              )}
              <Col md={5}>
                <div style={{ overflowWrap: 'anywhere' }}>
                  {' '}
                  <b>{i18n.t('wizard:details.congrats.endpoint')}</b>{' '}
                  {copyButton(props.endpoint, i18n.t('wizard:details.congrats.copyEndpointSuccessMsg'))}
                </div>
              </Col>
              <Col md={1}></Col>
            </Row>
          </div>

          <div style={{ marginTop: 40 }} className="text-center">
            {getDoneButton()}
          </div>
        </div>
      </div>
    );
  };

  const resolveMessageError = () => {
    if (props.deployError.includes('billing')) {
      return (
        <p style={{ marginBottom: '1px' }}>
          {' '}
          {i18n.t('wizard:details.created.message')}
          {isUserAccountOwner() ? (
            <a href={getBlazemeterBillingPageUrl(true)}>{i18n.t('wizard:details.created.administrator')}</a>
          ) : (
            i18n.t('wizard:details.created.accountAdmin')
          )}
        </p>
      );
    } else {
      return (
        <p style={{ marginBottom: '1px' }}>
          {i18n.t('wizard:details.created.error')}
          <br />
          {props.deployError}
        </p>
      );
    }
  };

  const created = () => {
    return (
      <>
        <div style={{ marginTop: 80 }} className="text-center">
          <h2>{i18n.t('wizard:details.created.title')}</h2>
        </div>

        <Burble>{resolveMessageError()}</Burble>

        <div style={{ marginTop: 40 }} className="text-center">
          <img src={relaxedsvg} alt={'relax'} />
        </div>

        <div style={{ marginTop: 40 }} className="text-center">
          <span>
            {' '}
            {i18n.t('wizard:details.created.instructions')}
            <Button style={{ padding: '0' }} className={'mb-1'} variant="link" onClick={props.onClose}>
              Virtual Services.
            </Button>
          </span>
        </div>

        <div style={{ marginTop: 40 }} className="text-center">
          {getDoneButton()}
        </div>
      </>
    );
  };

  const error = () => {
    return (
      <>
        <div style={{ marginTop: 80 }} className="text-center">
          <h2>{i18n.t('wizard:details.wzError.title')}</h2>
        </div>

        <div style={{ marginTop: 40 }} className="text-center">
          <img src={questionsvg} alt={'question'} />
        </div>

        <div style={{ marginTop: 40 }} className="text-center">
          <span>{i18n.t('wizard:details.wzError.message')}</span>
        </div>

        <div style={{ marginTop: 40 }} className="text-center">
          <Button className={'ml-3'} variant="primary" onClick={props.onClose}>
            {i18n.t('wizard:details.wzError.leave')}
          </Button>
        </div>
      </>
    );
  };

  switch (props.status) {
    case 'running':
      return running();
    case 'created':
      return created();
    case 'error':
      return error();
    default:
      return ready();
  }
};

export default Run;
